import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faTimesCircle as faTimesCircleSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonStandard, Chip, FormList, InputText, Text } from "@performyard/py-components";

import { Grid, GridCol } from "../../../../components/Grid";
import Spacer from "../../../../components/Spacer";

type DisallowedWordsListProps = {
	id: string;
	label: string;
	description: string;
};

type DisallowedWordChipProps = {
	word: string;
	onRemove: (word: string) => void;
};

const DisallowedWordChip = ({ word, onRemove }: DisallowedWordChipProps) => {
	return (
		<Chip
			color="primary"
			size={28}
			dangerouslySetClassName="w-fit"
			label={
				<div className="flex items-center gap-1">
					<Text size="paragraph-3" weight="regular" color="neutral-9" dangerouslySetClassName="mx-1">
						{word}
					</Text>
					<div
						onClick={() => onRemove(word)}
						className="z-0 flex flex-shrink-0 items-center justify-center text-xl text-primary-3 hover:cursor-pointer hover:text-primary-7 active:text-neutral-0"
					>
						<FontAwesomeIcon icon={faTimesCircleSolid} />
					</div>
				</div>
			}
		/>
	);
};

export const DisallowedWordsList = ({ id, label, description }: DisallowedWordsListProps) => {
	const [disallowedWordInput, setDisallowedWordInput] = useState<string>("");

	return (
		<GridCol span={5}>
			<FormList<string>
				id={id}
				labelPosition="top"
				label={
					<>
						<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
							{label}
						</Text>
						<Spacer height={1} />
						<div>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								{description}
							</Text>
						</div>
					</>
				}
				input={({ value, pushValue, removeValue }) => {
					const words = value || [];

					const handleAddDisallowedWord = () => {
						const trimmedWord = disallowedWordInput.trim();
						if (trimmedWord && !words.includes(trimmedWord)) {
							pushValue(trimmedWord);
							setDisallowedWordInput("");
						}
					};

					const handleRemoveDisallowedWord = (word: string) => {
						const index = words.indexOf(word);
						if (index !== -1) {
							removeValue(index);
						}
					};

					const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleAddDisallowedWord();
						}
					};

					return (
						<>
							<Grid cols={2} gap={1} className="mt-2 items-center">
								<GridCol width="1fr">
									<InputText
										value={disallowedWordInput}
										onChange={setDisallowedWordInput}
										onKeyDown={handleKeyDown}
										placeholder="Press 'enter' or click '+add' button"
									/>
								</GridCol>
								<GridCol width="70px">
									<ButtonStandard
										variant="solid"
										color="primary"
										size="medium"
										onClick={handleAddDisallowedWord}
										disabled={!disallowedWordInput.trim()}
										iconLeft={faPlus}
									>
										Add
									</ButtonStandard>
								</GridCol>
							</Grid>
							{words.length > 0 && (
								<div className="flex flex-wrap gap-2">
									{words.map((word) => (
										<DisallowedWordChip
											key={word}
											word={word}
											onRemove={handleRemoveDisallowedWord}
										/>
									))}
								</div>
							)}
						</>
					);
				}}
			/>
		</GridCol>
	);
};
